import { t } from "i18next";
import { useState, useEffect } from "react";
import { Modal, Button, Form, ListGroup } from "react-bootstrap";
import { PlanningRace, PlanningWeek } from "../../../../interfaces/planning";
import { createPlanningRace, updatePlanningRace, deletePlanningRace } from "../../../../services/planning";

/**
 * Props for the PlanningRaceModal component
 * @property show - Controls modal visibility
 * @property week - The planning week for which races are being managed
 * @property seasonId - The season ID (used when week.id is not available)
 * @property teamId - The team ID (used when week.id is not available)
 * @property onClose - Callback when modal is closed
 * @property onSave - Callback when races are saved, receives updated races array and optional new week id
 */
interface PlanningRaceModalProps {
    show: boolean;
    week: PlanningWeek | null;
    seasonId: number;
    teamId: number;
    onClose: () => void;
    onSave: (races: PlanningRace[], newWeekId?: number) => void;
}

/**
 * Modal component for managing races/competitions within a planning week.
 * Provides full CRUD functionality (Create, Read, Update, Delete) for races.
 *
 * Features:
 * - List view showing all races for the selected week
 * - Add new races with title and description
 * - Edit existing races
 * - Delete races with confirmation
 * - Smart date range formatting (handles same/different months)
 */
export function PlanningRaceModal(props: PlanningRaceModalProps) {
    // State for managing races list
    const [races, setRaces] = useState<PlanningRace[]>([]);

    // State for tracking which race is being edited (null = creating new)
    const [editingRace, setEditingRace] = useState<PlanningRace | null>(null);

    // Toggle between list view and form view
    const [showForm, setShowForm] = useState(false);

    // Form fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');

    /**
     * Formats the date range for display in the modal header.
     * Smart formatting:
     * - Same month: "14 al 20 de octubre"
     * - Different months: "28 de sep al 4 de oct"
     */
    const formatDateRange = () => {
        if (!props.week) return '';

        const fromDate = new Date(props.week.from);
        const toDate = new Date(props.week.to);

        const fromMonth = fromDate.toLocaleDateString('es-ES', { month: 'short' });
        const toMonth = toDate.toLocaleDateString('es-ES', { month: 'short' });

        const fromDay = props.week.from_name;
        const toDay = props.week.to_name;

        if (fromMonth === toMonth) {
            // Same month: "14 al 20 de octubre"
            return `${fromDay} ${t("to")} ${toDay} de ${toMonth}`;
        } else {
            // Different months: "28 de sep al 4 de oct"
            return `${fromDay} de ${fromMonth} ${t("to")} ${toDay} de ${toMonth}`;
        }
    };

    // Load races when the selected week changes
    useEffect(() => {
        setRaces(props.week?.races || []);
    }, [props.week]);

    /**
     * Initializes the form for creating a new race
     */
    const handleAddNew = () => {
        setEditingRace(null);
        setTitle('');
        setDescription('');
        setShowForm(true);
    };

    /**
     * Loads an existing race into the form for editing
     * @param race - The race to edit
     */
    const handleEdit = (race: PlanningRace) => {
        setEditingRace(race);
        setTitle(race.title);
        setDescription(race.description || '');
        setShowForm(true);
    };

    /**
     * Saves the race (create or update) and updates parent component state
     * Validates that title is not empty before saving
     * Uses season_id, team_id and week key when planning_week_id is not available
     */
    const handleSave = async () => {
        if (!title.trim()) {
            alert(t("Please enter a title for the race"));
            return;
        }

        if (!props.week) {
            alert(t("Cannot save race: week not selected"));
            return;
        }

        // Build race data - use planning_week_id if available, otherwise use season/team/week
        const raceData: any = {
            title: title.trim(),
            description: description.trim()
        };

        if (props.week.id) {
            raceData.planning_week_id = props.week.id;
        } else {
            // Use alternative format for weeks without id
            raceData.season_id = props.seasonId;
            raceData.team_id = props.teamId;
            raceData.week = props.week.key;
        }

        try {
            if (editingRace && editingRace.id) {
                // Update existing race
                raceData.id = editingRace.id;
                const response = await updatePlanningRace(raceData);

                if (response.data.status === 1) {
                    const updatedRaces = races.map(r =>
                        r.id === editingRace.id ? { ...raceData, id: editingRace.id, planning_week_id: response.data.data?.planning_week_id || raceData.planning_week_id } : r
                    );
                    setRaces(updatedRaces);
                    props.onSave(updatedRaces, response.data.data?.planning_week_id);
                } else {
                    console.error('Update race failed:', response.data);
                    alert(response.data.message || t("Error saving race. Please try again."));
                    return;
                }
            } else {
                // Create new race
                const response = await createPlanningRace(raceData);
                if (response.data.status === 1) {
                    const newWeekId = response.data.data?.planning_week_id;
                    const newRace = {
                        ...raceData,
                        id: response.data.data?.id || response.data.id,
                        planning_week_id: newWeekId || raceData.planning_week_id
                    };
                    const updatedRaces = [...races, newRace];
                    setRaces(updatedRaces);
                    props.onSave(updatedRaces, newWeekId);
                } else {
                    console.error('Create race failed:', response.data);
                    alert(response.data.message || t("Error saving race. Please try again."));
                    return;
                }
            }

            setShowForm(false);
            setTitle('');
            setDescription('');
            setEditingRace(null);
        } catch (error) {
            console.error('Error saving race:', error);
            alert(t("Error saving race. Please try again."));
        }
    };

    /**
     * Deletes a race after user confirmation
     * Updates parent component state after successful deletion
     * @param race - The race to delete
     */
    const handleDelete = async (race: PlanningRace) => {
        if (!window.confirm(t("Are you sure you want to delete this race?"))) {
            return;
        }

        try {
            if (race.id) {
                const response = await deletePlanningRace(race.id);
                if (response.data.status === 1) {
                    const updatedRaces = races.filter(r => r.id !== race.id);
                    setRaces(updatedRaces);
                    props.onSave(updatedRaces);
                } else {
                    console.error('Delete race failed:', response.data);
                    alert(response.data.message || t("Error deleting race. Please try again."));
                }
            }
        } catch (error) {
            console.error('Error deleting race:', error);
            alert(t("Error deleting race. Please try again."));
        }
    };

    /**
     * Cancels the form and returns to list view
     * Resets all form fields and editing state
     */
    const handleCancelForm = () => {
        setShowForm(false);
        setTitle('');
        setDescription('');
        setEditingRace(null);
    };

    return (
        <Modal show={props.show} onHide={props.onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("Races")} - {props.week?.key}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!showForm && (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>
                                {t("Race list")}
                                {props.week && (
                                    <small className="text-muted ms-2">
                                        {formatDateRange()}
                                    </small>
                                )}
                            </h5>
                            <Button variant="primary" size="sm" onClick={handleAddNew}>
                                <i className="bi bi-plus-circle me-2"></i>
                                {t("Add race")}
                            </Button>
                        </div>

                        {races.length === 0 ? (
                            <p className="text-muted">{t("No races scheduled for this week")}</p>
                        ) : (
                            <ListGroup>
                                {races.map((race, index) => (
                                    <ListGroup.Item key={race.id || index} className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">{race.title}</h6>
                                            {race.description && (
                                                <p className="mb-0 text-muted small">{race.description}</p>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleEdit(race)}>
                                                <i className="bi bi-pencil-square"></i>
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(race)}>
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </>
                )}

                {showForm && (
                    <div className="race-form">
                        <h5 className="mb-3">
                            {editingRace ? t("Edit race") : t("New race")}
                        </h5>
                        <Form>
                            <Form.Group className="mb-3" controlId="raceTitle">
                                <Form.Label>{t("Title")} *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t("e.g., National Championship")}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="raceDescription">
                                <Form.Label>{t("Description")}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t("Additional details about the race")}
                                />
                            </Form.Group>

                            <div className="d-flex gap-2">
                                <Button variant="primary" onClick={handleSave}>
                                    {t("Save")}
                                </Button>
                                <Button variant="secondary" onClick={handleCancelForm}>
                                    {t("Cancel")}
                                </Button>
                            </div>
                        </Form>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
